import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { MapPin, Clock, Monitor, User, Phone, ArrowLeft, Home, Car } from 'lucide-react';
import SEO from '../components/SEO';
import { tutorService } from '../services/tutorService';
import { getMediaUrl } from '../services/api';
import { trackContactClick } from '../utils/analytics';
import { clearEditProfileDraft } from '../utils/editProfileDraft';
import './TutorProfile.css';

const TutorProfile = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const previewMode = searchParams.get('preview') === 'true';
  const draftPreviewTutor = previewMode ? location.state?.previewTutor : null;

  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullNumber, setShowFullNumber] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState('');

  useEffect(() => {
    // Detect if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchTutorData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Support both /tutor/:id and /specialist/:slug URLs
        const tutorId = id || (slug ? slug.split('-').pop() : null);
        
        if (!tutorId) {
          setError('Էջը չի գտնվել');
          return;
        }

        if (previewMode && draftPreviewTutor) {
          setTutor(draftPreviewTutor);
          return;
        }

        const data = await tutorService.fetchTutorById(tutorId, { preview: previewMode });
        setTutor(data);
      } catch (err) {
        console.error('Error fetching tutor:', err);
        setError('Էջը չի գտնվել');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTutorData();
  }, [id, slug, previewMode, draftPreviewTutor]);

  useEffect(() => {
    if (!tutor || previewMode || draftPreviewTutor) return;
    const key = `viewed_profile_${tutor.id}`;
    if (localStorage.getItem(key)) return;
    tutorService
      .recordProfileView(tutor.id)
      .then(() => {
        localStorage.setItem(key, '1');
      })
      .catch(() => {});
  }, [tutor, previewMode, draftPreviewTutor]);

  const handlePublish = async () => {
    if (!tutor) return;
    setPublishing(true);
    setPublishError('');
    try {
      await tutorService.publishMyTutor();
      clearEditProfileDraft(tutor.id);
      navigate(`/tutor/${tutor.id}`, { replace: true });
    } catch (e) {
      setPublishError(e.message || 'Չհաջողվեց հրապարակել');
    } finally {
      setPublishing(false);
    }
  };

  const parseExperienceYearsFromDisplay = (exp) => {
    if (exp == null) return 0;
    const m = String(exp).match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  };

  const handleConfirmApprovedPreview = async () => {
    if (!tutor) return;
    setPublishing(true);
    setPublishError('');
    try {
      await tutorService.updateTutor(tutor.id, {
        name: tutor.name.trim(),
        phone: tutor.phone.trim(),
        region: (tutor.region || '').trim(),
        city: (tutor.city || '').trim(),
        district: (tutor.district || '').trim(),
        hourlyRate: Number(tutor.hourlyRate) || 0,
        experience: parseExperienceYearsFromDisplay(tutor.experience),
        description: (tutor.description || '').trim(),
        additionalInfo: (tutor.additionalInfo || '').trim(),
        subjects: Array.isArray(tutor.subjects) ? tutor.subjects : [],
        modes: Array.isArray(tutor.modes) ? tutor.modes : [],
      });
      clearEditProfileDraft(tutor.id);
      navigate(`/tutor/${tutor.id}`, { replace: true });
    } catch (e) {
      setPublishError(e.message || 'Չհաջողվեց պահպանել');
    } finally {
      setPublishing(false);
    }
  };

  const navigateToEditProfile = () => {
    navigate('/edit-profile', {
      state: location.state?.editDraft
        ? { restoreDraft: location.state.editDraft }
        : undefined,
    });
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'Online':
        return 'Օնլայն';
      case 'In-Person':
        return 'Իր մոտ';
      case 'Both':
      case 'Student-Location':
        return 'Ձեր մոտ';
      default:
        return mode;
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'Online':
        return <Monitor size={18} />;
      case 'In-Person':
        return <Home size={18} />;
      case 'Both':
      case 'Student-Location':
        return <Car size={18} />;
      default:
        return <User size={18} />;
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) {
      return 'https://via.placeholder.com/200/e2e8f0/718096?text=Նկար+չկա';
    }
    return getMediaUrl(photo);
  };

  const formatAmdPrice = (amdPrice) => {
    return Math.round(amdPrice).toLocaleString('hy-AM').replace(/,/g, ' ');
  };

  const maskPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 7) {
      return `+374 ${cleaned.slice(3, 5)} XXX XXX`;
    }
    return phone;
  };

  const formatPhoneForLink = (phone) => {
    if (!phone) return '';
    // Remove all non-digit characters including +
    return phone.replace(/\D/g, '');
  };

  const getWhatsAppLink = (phone) => {
    const cleanPhone = formatPhoneForLink(phone);
    return `https://wa.me/${cleanPhone}`;
  };

  const getViberLink = (phone) => {
    const cleanPhone = formatPhoneForLink(phone);
    return `viber://chat?number=${cleanPhone}`;
  };

  const getTelegramLink = (username) => {
    if (!username) return '';
    const cleanUsername = username.replace('@', '').trim();
    return `https://t.me/${cleanUsername}`;
  };

  const handleCallClick = async () => {
    trackContactClick('call', tutor.id, tutor.name);
    const key = `called_profile_${tutor.id}`;
    if (!localStorage.getItem(key)) {
      try {
        await tutorService.recordCallClick(tutor.id);
        localStorage.setItem(key, '1');
      } catch (_) {
        /* non-blocking */
      }
    }
    if (isMobile) {
      window.location.href = `tel:${tutor.phone}`;
    } else {
      setShowFullNumber(true);
    }
  };

  const openMessengerHref = (href) => {
    if (href.startsWith('viber:')) {
      window.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleMessagingClick = async (e, type, href) => {
    e.preventDefault();
    trackContactClick(type, tutor.id, tutor.name);
    const storageKey = `${type}_profile_${tutor.id}`;
    if (!localStorage.getItem(storageKey)) {
      try {
        await tutorService.recordMessageClick(tutor.id, type);
        localStorage.setItem(storageKey, '1');
      } catch (_) {
        /* non-blocking */
      }
    }
    openMessengerHref(href);
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-state">
          <p>Բեռնում է...</p>
        </div>
      </div>
    );
  }

  if (error || !tutor) {
    return (
      <div className="profile-page">
        <div className="error-state">
          <h2>Էջը չի գտնվել</h2>
          <button onClick={() => navigate('/')} className="back-home-btn">
            Վերադառնալ գլխավոր էջ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {tutor && (
        <SEO 
          title={`${tutor.name} - ${(tutor.subjects || []).join(', ')}`}
          description={`${(tutor.description || '').substring(0, 150)}... | ${tutor.location} | ${tutor.hourlyRate} դրամ/ժամ`}
          image={tutor.photo ? getMediaUrl(tutor.photo) : undefined}
          url={`/specialist/${tutor.name.toLowerCase().replace(/\s+/g, '-')}-${tutor.id}`}
          type="profile"
        />
      )}

      {previewMode && tutor && (
        <div className="profile-preview-banner" role="region" aria-label="Նախադիտում">
          <p className="profile-preview-banner-text">Դուք դիտում եք ձեր պրոֆիլի նախադիտումը</p>
          {publishError && <p className="profile-preview-banner-error">{publishError}</p>}
          <div className="profile-preview-banner-actions">
            <button
              type="button"
              className="profile-preview-btn profile-preview-btn-secondary"
              onClick={navigateToEditProfile}
            >
              Խմբագրել
            </button>
            {tutor.status !== 'approved' ? (
              <button
                type="button"
                className="profile-preview-btn profile-preview-btn-primary"
                onClick={handlePublish}
                disabled={publishing}
              >
                {publishing ? 'Հրապարակվում է...' : 'Հաստատել և հրապարակել'}
              </button>
            ) : (
              <button
                type="button"
                className="profile-preview-btn profile-preview-btn-primary"
                onClick={handleConfirmApprovedPreview}
                disabled={publishing}
              >
                {publishing ? 'Պահպանվում է...' : 'Հաստատել'}
              </button>
            )}
          </div>
        </div>
      )}
      
      <div className="profile-wrapper">
        <button
          type="button"
          onClick={() => (previewMode ? navigateToEditProfile() : navigate('/'))}
          className="back-link"
        >
          <ArrowLeft size={18} />
          <span>Վերադառնալ</span>
        </button>

        {/* Hero Card */}
        <div className="hero-card">
          <div className="hero-left">
            <img
              src={getPhotoUrl(tutor.photo)}
              alt={tutor.name}
              className="profile-avatar"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/200/e2e8f0/718096?text=Նկար+չկա';
              }}
            />
          </div>

          <div className="hero-right">
            <h1 className="profile-name">{tutor.name}</h1>

            {/* Subjects */}
            <div className="subjects-tags">
              {tutor.subjects && tutor.subjects.map((subject, index) => (
                <span key={index} className="subject-tag">{subject}</span>
              ))}
            </div>

            {/* Location */}
            <div className="info-row">
              <MapPin size={18} />
              <span>{tutor.location}</span>
            </div>

            {/* Modes */}
            {tutor.modes && tutor.modes.length > 0 && (
              <div className="modes-row">
                {tutor.modes.map((mode, index) => (
                  <div key={index} className="mode-item">
                    {getModeIcon(mode)}
                    <span>{getModeLabel(mode)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Experience */}
            <div className="info-row">
              <Clock size={18} />
              <span>{tutor.experience}</span>
            </div>

            {/* Price Badge */}
            <div className="price-badge">
              💰 {formatAmdPrice(tutor.hourlyRate)} դրամ / ժամ
            </div>
          </div>

          {/* CTA Section */}
          <div className="cta-section">
            {!showFullNumber ? (
              <>
                <button onClick={handleCallClick} className="call-button primary">
                  <Phone size={20} />
                  <span>Զանգել</span>
                </button>
                {!isMobile && (
                  <p className="masked-number">{maskPhoneNumber(tutor.phone)}</p>
                )}
              </>
            ) : (
              <div className="revealed-number">
                <a href={`tel:${tutor.phone}`} className="full-number-link">
                  <Phone size={20} />
                  <span>{tutor.phone}</span>
                </a>
              </div>
            )}

            {/* Secondary Messaging Buttons */}
            {(tutor.supportsWhatsapp || tutor.supportsViber || tutor.telegramUsername) && (
              <div className="messaging-buttons">
                {tutor.supportsWhatsapp && (
                  <a
                    href={getWhatsAppLink(tutor.contact?.phone || tutor.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="messaging-btn whatsapp"
                    title="WhatsApp"
                    onClick={(e) =>
                      handleMessagingClick(
                        e,
                        'whatsapp',
                        getWhatsAppLink(tutor.contact?.phone || tutor.phone)
                      )
                    }
                  >
                    💬 WhatsApp
                  </a>
                )}
                {tutor.supportsViber && (
                  <a
                    href={getViberLink(tutor.contact?.phone || tutor.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="messaging-btn viber"
                    title="Viber"
                    onClick={(e) =>
                      handleMessagingClick(
                        e,
                        'viber',
                        getViberLink(tutor.contact?.phone || tutor.phone)
                      )
                    }
                  >
                    📱 Viber
                  </a>
                )}
                {tutor.telegramUsername && (
                  <a
                    href={getTelegramLink(tutor.telegramUsername)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="messaging-btn telegram"
                    title="Telegram"
                    onClick={(e) =>
                      handleMessagingClick(
                        e,
                        'telegram',
                        getTelegramLink(tutor.telegramUsername)
                      )
                    }
                  >
                    ✈️ Telegram
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Description Section */}
        <section className="content-section">
          <h2>Նկարագրություն</h2>
          <p className="description-text">{tutor.description}</p>
        </section>

        {/* Additional Info Section (only if exists) */}
        {tutor.additionalInfo && (
          <section className="content-section">
            <h2>Լրացուցիչ տեղեկություն</h2>
            <p className="additional-info-text">{tutor.additionalInfo}</p>
          </section>
        )}
      </div>
    </div>
  );
};

export default TutorProfile;
